/**
 * Mapeo de los design tokens Go Electric a `theme.token` de Ant Design v5 (ADR 0015).
 *
 * El Admin Web usa `ConfigProvider` con el resultado de `createAdminTheme(resolvedTheme)`.
 * Toda la marca vive en `tokens.dtcg.json`; aquí solo se traduce a los nombres de antd.
 *
 * Decisiones del rediseño v2 (Guía §7):
 * - Shell unificado: en oscuro, fondo, sidebar y header comparten `bg-dark` (#050c14);
 *   la elevación se define con borde de 1px (`border-dark`), no con sombras.
 * - `colorPrimary` en oscuro pasa a `primary-dark` (#1279b0), el teal de marca aclarado:
 *   el navy no contrasta sobre near-black y el blanco encima da 4.79:1.
 * - `colorBgElevated` usa `surface-elevated-dark` (#12263a) para que modales, dropdowns
 *   y popovers se despeguen del fondo.
 * - Los semánticos tienen variante dark propia (success #35a46e, warning #e8a33d,
 *   danger #e5604c) y cumplen AA sobre `surface-dark` sin parches por pantalla.
 * - El oro (#f4c84b) se reserva como **acento** (foco, KPI, highlights); nunca como
 *   color de acción primaria (regla accent-only).
 */
import { theme as antdTheme } from 'antd';
import type { ThemeConfig } from 'antd';
import {
  brandColors,
  darkColors,
  neutralColors,
  radius,
  semanticColors,
  shadows,
  shellColors,
  typography,
  type ThemeName,
} from '../tokens';

export function createAdminTheme(mode: ThemeName): ThemeConfig {
  const isDark = mode === 'dark';
  const controlBorder = isDark ? darkColors.controlBorder : neutralColors.controlBorder;
  // El darkAlgorithm de antd mapea `colorPrimary` a `generate(seed,{theme:'dark'})[5]`, que para
  // nuestro seed #1279b0 da #126a99 (2.97:1 sobre surface-dark). Se fija por componente para que
  // el foco use el primario exacto (#1279b0, 3.68:1; WCAG 1.4.11).
  const primary = isDark ? darkColors.primary : brandColors.primary;
  // Opción seleccionada del Select: `colorPrimaryBg` derivado del navy sale gris (#859194)
  // y el texto por defecto (`colorText`) queda ilegible encima. Se usa un tinte del primario
  // con texto de alto contraste.
  const optionSelectedBg = isDark
    ? `color-mix(in srgb, ${darkColors.primary} 24%, ${darkColors.surface})`
    : `color-mix(in srgb, ${brandColors.primary} 12%, ${neutralColors.surface})`;
  const optionSelectedColor = isDark ? darkColors.text : brandColors.primary;

  const sharedToken: ThemeConfig['token'] = {
    borderRadius: Number.parseInt(radius.default.$value, 10),
    borderRadiusLG: Number.parseInt(radius.lg.$value, 10),
    fontFamily: typography.fontFamilySans,
    fontSize: Number.parseInt(typography.fontSize.body.$value, 10),
    fontSizeSM: Number.parseInt(typography.fontSize.compact.$value, 10),
    fontWeightStrong: Number(typography.fontWeight.semibold.$value),
    controlHeight: 44,
    controlHeightSM: 40,
    controlHeightLG: 52,
    wireframe: false,
  };

  const lightToken: ThemeConfig['token'] = {
    ...sharedToken,
    colorPrimary: brandColors.primary,
    colorPrimaryHover: brandColors.primaryHover,
    colorInfo: brandColors.info,
    colorSuccess: semanticColors.success,
    colorError: semanticColors.danger,
    colorWarning: semanticColors.warning,
    colorLink: brandColors.info,
    // Anillo de foco de los controles: primario con alpha (no el `colorPrimaryBg` derivado,
    // que para el navy sale gris).
    controlOutline: `color-mix(in srgb, ${brandColors.primary} 16%, transparent)`,
    colorBgLayout: neutralColors.bg,
    colorBgContainer: neutralColors.surface,
    colorBgElevated: neutralColors.surface,
    colorBorder: neutralColors.border,
    colorBorderSecondary: neutralColors.border,
    colorText: neutralColors.text,
    colorTextHeading: brandColors.primary,
    colorTextSecondary: neutralColors.textMuted,
    // `colorTextDescription` (Typography `type="secondary"`, empty-states, captions) es un
    // alias de `colorTextTertiary`. El `defaultAlgorithm` lo deriva de `colorTextBase` con
    // alpha 0.45 → #8c8c8c sobre blanco (3.36:1, bajo AA). Se fija al texto secundario del
    // tema (#66747e, 4.81:1 sobre blanco) para cumplir AA.
    colorTextTertiary: neutralColors.textMuted,
  };

  const darkToken: ThemeConfig['token'] = {
    ...sharedToken,
    colorPrimary: darkColors.primary,
    colorPrimaryHover: darkColors.primaryHover,
    colorInfo: darkColors.info,
    colorSuccess: darkColors.success,
    colorError: darkColors.danger,
    colorWarning: darkColors.warning,
    colorLink: darkColors.link,
    controlOutline: `color-mix(in srgb, ${darkColors.primary} 30%, transparent)`,
    colorBgLayout: darkColors.bg,
    colorBgContainer: darkColors.surface,
    colorBgElevated: darkColors.surfaceElevated,
    colorBorder: darkColors.border,
    colorBorderSecondary: darkColors.border,
    colorText: darkColors.text,
    colorTextHeading: darkColors.text,
    colorTextSecondary: darkColors.textMuted,
    // `colorTextDescription` es un alias de `colorTextTertiary` en antd. El `darkAlgorithm`
    // lo deriva de `colorTextBase` con alpha 0.45 → rgba(255,255,255,.45) compuesto sobre
    // `surface-dark` da #798189 (4.45:1, apenas bajo AA). Subtítulos, captions, labels de
    // tabla y empty-states usan este token, así que se fija al texto secundario del tema
    // (#a7bbcb, 8.90:1 sobre surface-dark) para cumplir AA.
    colorTextTertiary: darkColors.textMuted,
  };

  // El sidebar usa `theme="dark"` del Menu en ambos temas (siempre es oscuro).
  // En claro conserva el navy con acento oro; en oscuro, pill relleno con primary-dark.
  const menuTokens = isDark
    ? {
        darkItemBg: 'transparent',
        darkSubMenuItemBg: 'transparent',
        darkItemColor: darkColors.textMuted,
        darkItemHoverColor: darkColors.text,
        darkItemHoverBg: `color-mix(in srgb, ${darkColors.primary} 18%, transparent)`,
        darkItemSelectedBg: darkColors.primary,
        darkItemSelectedColor: '#ffffff',
        itemBorderRadius: Number.parseInt(radius.default.$value, 10),
        itemHeight: 44,
        itemMarginInline: 8,
      }
    : {
        darkItemBg: 'transparent',
        darkSubMenuItemBg: 'transparent',
        darkItemColor: shellColors.light.textMuted,
        darkItemHoverColor: shellColors.light.text,
        darkItemHoverBg: 'rgba(244,200,75,0.10)',
        darkItemSelectedBg: 'rgba(244,200,75,0.16)',
        darkItemSelectedColor: darkColors.accentOnDark,
        itemBorderRadius: Number.parseInt(radius.default.$value, 10),
        itemHeight: 44,
        itemMarginInline: 8,
      };

  return {
    algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: isDark ? darkToken : lightToken,
    components: {
      Layout: {
        headerBg: isDark ? darkColors.bg : 'transparent',
        bodyBg: isDark ? darkColors.bg : neutralColors.bg,
        siderBg: isDark ? darkColors.bg : shellColors.light.bg,
        headerHeight: 64,
        headerPadding: '0 24px',
      },
      Menu: menuTokens,
      Button: {
        controlHeight: 44,
        borderRadius: Number.parseInt(radius.default.$value, 10),
        fontWeight: 600,
        primaryShadow: 'none',
      },
      Table: {
        // Fondo opaco obligatorio: el `th` de las columnas fijas es sticky y, con scroll
        // horizontal, un header transparente dejaría ver el `th` de la columna vecina por debajo.
        // `darkColors.surface` coincide con `colorBgContainer`, así que en oscuro no cambia el color.
        headerBg: isDark ? darkColors.surface : neutralColors.bg,
        headerColor: isDark ? darkColors.textMuted : brandColors.primary,
        borderColor: isDark ? darkColors.border : neutralColors.border,
        rowHoverBg: isDark ? darkColors.surfaceHover : neutralColors.surfaceHover,
        cellPaddingBlock: 12,
        cellPaddingInline: 16,
      },
      Card: {
        borderRadiusLG: Number.parseInt(radius.lg.$value, 10),
        // En oscuro la elevación se define con borde, no con sombra.
        boxShadowTertiary: isDark ? 'none' : shadows.sm.$value,
      },
      Input: { controlHeight: 44, colorBorder: controlBorder, colorPrimary: primary },
      InputNumber: { colorBorder: controlBorder, colorPrimary: primary },
      Select: {
        controlHeight: 44,
        colorBorder: controlBorder,
        colorPrimary: primary,
        optionSelectedBg,
        optionSelectedColor,
      },
      DatePicker: { colorBorder: controlBorder, colorPrimary: primary },
      Checkbox: { colorBorder: controlBorder },
      Radio: { colorBorder: controlBorder },
      Modal: { borderRadiusLG: Number.parseInt(radius.lg.$value, 10) },
      Tooltip: { colorBgSpotlight: isDark ? darkColors.surfaceElevated : brandColors.primary },
    },
  };
}
